import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { logoWhite, tickets } from '@/assets/images'
import Container from '@/components/atoms/container/container'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import HomeCards from '@/components/organisms/homeCards/homeCards'
import { useNavigationContext } from '@/hooks/useNavigationHook'
import { useWindowSize } from '@/hooks/useWindowSize'
import { captureDiagnosticSnapshot } from '@/monitoring/diagnostics'
import {
    startReplayRecording,
    stopReplayRecording
} from '@/monitoring/sentry'
import { DeviceApiNetworkError } from '@/services/apiService'
import { AuthService } from '@/services/authService'
import { deviceTokenService } from '@/services/deviceTokenService'
import { POSService } from '@/services/posService'
import { Auth } from '@/types/common/httpRequest'

const RETRY_INTERVAL_SECONDS = 10

interface ConnectivityIssuesNavigationState {
    reason?: string
    error?: unknown
    source?: string
}

export default function ConnectivityIssuesTemplate() {
    const [countdown, setCountdown] = useState(RETRY_INTERVAL_SECONDS)
    const [isRetrying, setIsRetrying] = useState(false)
    const [retryAttempts, setRetryAttempts] = useState(0)
    const { height } = useWindowSize()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { startUrl } = useNavigationContext()

    const navigationState =
        (location.state as ConnectivityIssuesNavigationState | null) ?? null
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
        null
    )
    const attemptCounterRef = useRef(0)

    const clearTimers = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = null
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
        }
    }, [])

    // Capture a Sentry replay while the kiosk is on the connectivity-issues
    // page so we can see exactly what the user encountered and how recovery
    // played out. Logging the diagnostic snapshot - plus whatever reason the
    // navigating site passed via location state - makes the replay
    // self-explanatory: token state, API mode/base-url, online flag, whether
    // the local device API was the source, and which call kicked us here.
    useEffect(() => {
        startReplayRecording()
        console.log(
            '[connectivity-issues] Page entered - diagnostic snapshot:',
            captureDiagnosticSnapshot()
        )
        if (navigationState) {
            console.log(
                '[connectivity-issues] Navigated here because:',
                {
                    source: navigationState.source ?? '(unknown)',
                    reason: navigationState.reason ?? '(unknown)'
                },
                navigationState.error
            )
        } else {
            console.log(
                '[connectivity-issues] No navigation reason provided (direct nav, refresh, or legacy caller).'
            )
        }
        return () => {
            void stopReplayRecording()
        }
    }, [navigationState])

    const attemptRecovery = useCallback(async () => {
        attemptCounterRef.current += 1
        const attemptNumber = attemptCounterRef.current
        setRetryAttempts(attemptNumber)
        setIsRetrying(true)

        console.log(
            `[connectivity-issues] Recovery attempt #${attemptNumber} starting -`,
            captureDiagnosticSnapshot()
        )

        // Step 1: without a device token, keep trying the device API (backup
        // when localStorage was wiped). Never go to /register unless both
        // localStorage AND device API are empty with no prior marker.
        if (!AuthService.HasToken(Auth.POS)) {
            try {
                console.log(
                    `[connectivity-issues] Attempt #${attemptNumber} step=local-api - reloading device token (networkError=${deviceTokenService.hasLocalApiNetworkError()}, apiEmpty=${deviceTokenService.hasLocalApiRespondedEmpty()}, priorRegistration=${deviceTokenService.hasBeenRegistered()})...`
                )
                const reloadedToken = await deviceTokenService.reloadFromLocalApi()

                if (reloadedToken) {
                    console.log(
                        `[connectivity-issues] Attempt #${attemptNumber} step=local-api - token restored to localStorage`
                    )
                } else if (deviceTokenService.hasLocalApiNetworkError()) {
                    console.error(
                        `[connectivity-issues] Attempt #${attemptNumber} step=local-api - device API still unreachable, will retry`
                    )
                    setIsRetrying(false)
                    setCountdown(RETRY_INTERVAL_SECONDS)
                    return
                } else if (deviceTokenService.shouldNavigateToRegister()) {
                    console.warn(
                        `[connectivity-issues] Attempt #${attemptNumber} - no token anywhere and no registration marker; redirecting to /register`
                    )
                    clearTimers()
                    navigate('/register', {
                        state: {
                            source: 'connectivityIssues.recovery',
                            reason: 'Genuinely unregistered: empty localStorage and empty device API response'
                        }
                    })
                    return
                } else {
                    console.warn(
                        `[connectivity-issues] Attempt #${attemptNumber} - device API empty but kiosk was registered before (marker/token history); will keep retrying or use debug nuke`
                    )
                    setIsRetrying(false)
                    setCountdown(RETRY_INTERVAL_SECONDS)
                    return
                }
            } catch (error) {
                if (error instanceof DeviceApiNetworkError) {
                    console.error(
                        `[connectivity-issues] Attempt #${attemptNumber} step=local-api - unreachable, will retry:`,
                        error
                    )
                } else {
                    console.error(
                        `[connectivity-issues] Attempt #${attemptNumber} step=local-api - unexpected error:`,
                        error
                    )
                }
                setIsRetrying(false)
                setCountdown(RETRY_INTERVAL_SECONDS)
                return
            }
        } else {
            // Stale or rejected token in localStorage: device API is source of truth.
            try {
                console.log(
                    `[connectivity-issues] Attempt #${attemptNumber} step=local-api-refresh - reloading device token over cached localStorage copy...`
                )
                const reloadedToken = await deviceTokenService.reloadFromLocalApi()

                if (!reloadedToken) {
                    if (deviceTokenService.hasLocalApiNetworkError()) {
                        console.error(
                            `[connectivity-issues] Attempt #${attemptNumber} step=local-api-refresh - device API unreachable, will retry`
                        )
                        setIsRetrying(false)
                        setCountdown(RETRY_INTERVAL_SECONDS)
                        return
                    }
                    if (deviceTokenService.hasBeenRegistered()) {
                        console.warn(
                            `[connectivity-issues] Attempt #${attemptNumber} step=local-api-refresh - device API empty but kiosk was registered; will keep retrying`
                        )
                        setIsRetrying(false)
                        setCountdown(RETRY_INTERVAL_SECONDS)
                        return
                    }
                }
            } catch (error) {
                if (error instanceof DeviceApiNetworkError) {
                    console.error(
                        `[connectivity-issues] Attempt #${attemptNumber} step=local-api-refresh - unreachable, will retry:`,
                        error
                    )
                    setIsRetrying(false)
                    setCountdown(RETRY_INTERVAL_SECONDS)
                    return
                }
                console.error(
                    `[connectivity-issues] Attempt #${attemptNumber} step=local-api-refresh - unexpected error:`,
                    error
                )
            }
        }

        // Step 2: with a device token in hand, retry creating the cloud
        // session. We deliberately use forceRecreateSession instead of
        // createSession - the latter short-circuits and returns the cached
        // Cashier token without making any network call, which would cause
        // the page to bounce straight back to /welcome even while the device
        // is offline. forceRecreateSession always hits the cloud, so its
        // success is a real connectivity signal. It also preserves the
        // previous Cashier token on failure so any in-flight authenticated
        // requests keep working.
        try {
            console.log(
                `[connectivity-issues] Attempt #${attemptNumber} step=force-recreate-session - hitting cloud to verify connectivity...`
            )
            await POSService.forceRecreateSession()
            console.log(
                `[connectivity-issues] Attempt #${attemptNumber} step=force-recreate-session - succeeded, navigating to welcome`
            )

            clearTimers()
            navigate(startUrl ?? '/welcome-with-services')
        } catch (error) {
            console.error(
                `[connectivity-issues] Attempt #${attemptNumber} step=force-recreate-session - failed, will retry:`,
                error
            )
            setIsRetrying(false)
            setCountdown(RETRY_INTERVAL_SECONDS)
        }
    }, [clearTimers, navigate, startUrl])

    // Initial attempt on mount
    useEffect(() => {
        attemptRecovery()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Countdown and retry logic
    useEffect(() => {
        if (isRetrying) return

        countdownIntervalRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    return RETRY_INTERVAL_SECONDS
                }
                return prev - 1
            })
        }, 1000)

        retryTimeoutRef.current = setTimeout(() => {
            attemptRecovery()
        }, RETRY_INTERVAL_SECONDS * 1000)

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current)
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
            }
        }
    }, [isRetrying, attemptRecovery])

    return (
        <Container style={{ gap: 0 }} isFullHeight={true}>
            <Header isWelcome={true} />
            <div
                className="connectivity-issues-content"
                style={{ maxHeight: height }}>
                <div className="home-cards-container">
                    <HomeCards isAnimating={false} />
                </div>

                <div className="connectivity-main">
                    <div className="vcash-logo">
                        <img src={logoWhite} alt="" />
                    </div>

                    <svg
                        className="connectivity-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2">
                        <path d="M12 20h.01" />
                        <path d="M8.5 16.429a5 5 0 0 1 7 0" />
                        <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
                        <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
                        <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
                        <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
                        <path d="m2 2 20 20" />
                    </svg>

                    <h1>{t('connectivityIssues.title')}</h1>
                    <p>{t('connectivityIssues.text')}</p>

                    <div className="retry-info">
                        {isRetrying ? (
                            <>
                                <div className="retry-spinner" />
                                <span className="retry-text">
                                    {t('connectivityIssues.retrying')}
                                </span>
                            </>
                        ) : (
                            <>
                                <div className="retry-spinner" />
                                <span className="retry-text">
                                    {t('connectivityIssues.autoRetry')}
                                </span>
                                <span className="retry-countdown">
                                    {t('connectivityIssues.retryIn', {
                                        seconds: countdown
                                    })}
                                </span>
                            </>
                        )}
                        {retryAttempts > 0 && (
                            <span className="retry-attempts">
                                {t('connectivityIssues.attempts', {
                                    count: retryAttempts
                                })}
                            </span>
                        )}
                    </div>
                </div>

                <div className="tickets-container">
                    <img src={tickets} alt="" className="tickets-image" />
                </div>
            </div>
            <Footer isWelcome={true} />
        </Container>
    )
}
